import React, { useState, useEffect } from 'react';
import {
  Shield, X, Activity, Lock, ChevronDown, ChevronRight,
  Eye, Zap, LayoutDashboard, ClipboardList, Calendar,
  Banknote, FileText, Users, Settings, BarChart3,
  UserPlus, Stethoscope, Building2, Share2, PhoneCall,
  CheckCircle2, Circle, Webhook, HeartHandshake
} from 'lucide-react';
import { getPermissionsApi, createRoleApi, updateRoleApi } from '../../services/roleApi';

// ─── Module metadata: icon, color, display label ───────────────────────────
const MODULE_META = {
  lead:           { label: 'Lead Management',       icon: ClipboardList, color: 'blue',    bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',    badge: 'bg-blue-100 text-blue-700' },
  appointment:    { label: 'Appointments',          icon: Calendar,      color: 'indigo',  bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700',  badge: 'bg-indigo-100 text-indigo-700' },
  surgery:        { label: 'Surgical Pipeline',     icon: Activity,      color: 'rose',    bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700',    badge: 'bg-rose-100 text-rose-700' },
  payout:         { label: 'Disbursals & Payouts',  icon: Banknote,      color: 'emerald', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
  reports:        { label: 'Reports & Analytics',   icon: BarChart3,     color: 'amber',   bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   badge: 'bg-amber-100 text-amber-700' },
  user:           { label: 'System Users',          icon: Users,         color: 'violet',  bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700',  badge: 'bg-violet-100 text-violet-700' },
  role:           { label: 'Role & Permissions',    icon: Shield,        color: 'slate',   bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700',   badge: 'bg-slate-100 text-slate-600' },
  ophe:           { label: 'OPHE Services',         icon: HeartHandshake,color: 'teal',    bg: 'bg-teal-50',    border: 'border-teal-200',    text: 'text-teal-700',    badge: 'bg-teal-100 text-teal-700' },
  call_analytics: { label: 'Call Analytics',        icon: PhoneCall,     color: 'teal',    bg: 'bg-teal-50',    border: 'border-teal-200',    text: 'text-teal-700',    badge: 'bg-teal-100 text-teal-700' },
  webhook:        { label: 'Webhooks & Integrations', icon: Webhook,     color: 'purple',  bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-700',  badge: 'bg-purple-100 text-purple-700' },
};

// ─── Sidebar permission definitions (displayed in their own section) ────────
const SIDEBAR_PERMISSIONS = [
  { key: 'sidebar:lead_management',    label: 'Lead Management',          icon: ClipboardList,    desc: 'Show/hide the Lead Management section' },
  { key: 'sidebar:appointments',       label: 'Appointments',             icon: Calendar,         desc: 'Show/hide the Appointments section' },
  { key: 'sidebar:surgical_pipeline',  label: 'Surgical Pipeline',        icon: Activity,         desc: 'Show/hide the Surgical Pipeline section' },
  { key: 'sidebar:call_analytics',     label: 'Call Analytics',           icon: PhoneCall,        desc: 'Show/hide Call Analytics section' },
  { key: 'sidebar:reports',            label: 'Reports',                  icon: FileText,         desc: 'Show/hide Reports link' },
  { key: 'sidebar:performance_reports',label: 'Performance Reports',      icon: BarChart3,        desc: 'Show/hide Performance Reports link' },
  { key: 'sidebar:disbursals',         label: 'Disbursals & Payouts',     icon: Banknote,         desc: 'Show/hide Disbursals & Payouts section' },
  { key: 'sidebar:activity_logs',      label: 'Activity Logs',            icon: Activity,         desc: 'Show/hide Activity Logs link' },
  { key: 'sidebar:onboarding',         label: 'Onboarding',               icon: UserPlus,         desc: 'Show/hide Onboarding section' },
  { key: 'sidebar:roster',             label: 'Roster & Follow-ups',      icon: Calendar,         desc: 'Show/hide Roster Management links' },
  { key: 'sidebar:ophe_services',      label: 'OPHE Services',            icon: HeartHandshake,   desc: 'Show/hide OPHE Services section' },
  { key: 'sidebar:doctors',            label: 'Doctors Directory',        icon: Stethoscope,      desc: 'Show/hide Doctors directory link' },
  { key: 'sidebar:hospitals',          label: 'Hospitals Directory',      icon: Building2,        desc: 'Show/hide Hospitals directory link' },
  { key: 'sidebar:lead_partners',      label: 'Lead Partners',            icon: Share2,           desc: 'Show/hide Lead Partners directory link' },
  { key: 'sidebar:system_users',       label: 'System Users',             icon: Users,            desc: 'Show/hide System Users link' },
  { key: 'sidebar:roles',              label: 'Role & Permissions',       icon: Shield,           desc: 'Show/hide Role & Permissions link' },
  { key: 'sidebar:webhooks',           label: 'Webhooks & API',           icon: Webhook,          desc: 'Show/hide Webhooks & Integrations link' },
  { key: 'sidebar:support',            label: 'Support & Help',           icon: PhoneCall,        desc: 'Show/hide Support & Help link' },
  { key: 'sidebar:settings',           label: 'Settings',                 icon: Settings,         desc: 'Show/hide Settings link' },
];

// Human readable for module action permissions
const PERM_LABELS = {
  'appointment:create':    { label: 'Create Appointments',             desc: 'Book new appointments for patients' },
  'appointment:read':      { label: 'View Appointments',               desc: 'View appointment calendar & list' },
  'appointment:update':    { label: 'Edit Appointments',               desc: 'Reschedule and modify appointments' },
  'appointment:delete':    { label: 'Cancel Appointments',             desc: 'Cancel scheduled appointments' },
  'lead:create':           { label: 'Create Leads',                    desc: 'Register new patient enquiries' },
  'lead:read_all':         { label: 'View All Leads',                  desc: 'Access all leads across system' },
  'lead:read_assigned':    { label: 'View Assigned Leads',             desc: 'View only leads assigned to me' },
  'lead:update':           { label: 'Update Leads',                    desc: 'Edit patient details & pipeline stage' },
  'lead:assign':           { label: 'Assign Leads',                    desc: 'Assign to staff, doctors & hospitals' },
  'lead:delete':           { label: 'Delete Leads',                    desc: 'Archive or delete lead records' },
  'surgery:view':          { label: 'View Surgeries',                  desc: 'Access surgical pipeline & OT cases' },
  'surgery:schedule':      { label: 'Schedule Surgeries',              desc: 'Book OT slots & surgery timings' },
  'surgery:update_status': { label: 'Update Surgery Status',           desc: 'Advance surgery workflow stages' },
  'surgery:verify_otp':    { label: 'Verify Surgery OTP',              desc: 'Confirm pre-op/post-op OTP codes' },
  'payout:view_own':       { label: 'View My Payouts',                 desc: 'See own earnings & payout history' },
  'payout:view_all':       { label: 'View All Payouts',                desc: 'Access all partner & doctor payouts' },
  'payout:approve':        { label: 'Approve Payouts',                 desc: 'Approve pending payout requests' },
  'payout:process':        { label: 'Process Payments',                desc: 'Execute bank disbursements' },
  'report:view':           { label: 'View Reports',                    desc: 'Access performance dashboards' },
  'report:export':         { label: 'Export Reports',                  desc: 'Download CSV/Excel reports' },
  'user:view':             { label: 'View Users',                      desc: 'Browse staff & partner directory' },
  'user:create':           { label: 'Create Users',                    desc: 'Invite & onboard new users' },
  'user:update':           { label: 'Update Users',                    desc: 'Edit user profiles and roles' },
  'user:delete':           { label: 'Deactivate Users',                desc: 'Remove or suspend user accounts' },
  'role:manage':           { label: 'Manage Roles',                    desc: 'Create & configure custom roles' },
  'ophe:view':             { label: 'View OPHE Services',              desc: 'View OPHE categories, packages & services' },
  'ophe:create':           { label: 'Create OPHE Services',            desc: 'Add new OPHE categories and services' },
  'ophe:update':           { label: 'Update OPHE Services',            desc: 'Edit OPHE categories, pricing & services' },
  'ophe:delete':           { label: 'Delete OPHE Services',            desc: 'Delete OPHE categories and services' },
  'call_history:view_own': { label: 'View Own Calls',                  desc: 'Access personal call history only' },
  'call_history:view_all': { label: 'View All Calls',                  desc: 'Access all call history across system' },
  'call:initiate':         { label: 'Initiate Calls',                  desc: 'Make outbound click-to-call calls' },
  'call:receive':          { label: 'Receive Calls',                   desc: 'Receive inbound customer calls' },
  'webhook:view':          { label: 'View Webhooks',                   desc: 'View inbound/outbound webhooks & logs' },
  'webhook:manage':        { label: 'Manage Webhooks',                 desc: 'Create, update, and delete webhooks' },
  'webhook:test':          { label: 'Test Webhooks',                   desc: 'Test ping & simulate webhook events' },
};

// ─── Module order for rendering ────────────────────────────────────────────
const MODULE_ORDER = ['lead', 'appointment', 'surgery', 'call_analytics', 'ophe', 'payout', 'reports', 'user', 'role', 'webhook'];

export default function CreateRoleModal({ isOpen, onClose, roleToEdit, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [permissionsGrouped, setPermissionsGrouped] = useState({});
  const [allPermissions, setAllPermissions] = useState([]);
  const [appointmentStages, setAppointmentStages] = useState([]);
  const [surgicalStages, setSurgicalStages] = useState([]);

  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [hierarchyLevel, setHierarchyLevel] = useState(50);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const [apptViewStages, setApptViewStages] = useState([]);
  const [apptUpdateStages, setApptUpdateStages] = useState([]);
  const [surgViewStages, setSurgViewStages] = useState([]);
  const [surgUpdateStages, setSurgUpdateStages] = useState([]);

  const [activeTab, setActiveTab] = useState('sidebar');
  const [expandedModules, setExpandedModules] = useState({});

  useEffect(() => { if (isOpen) fetchPermissionsData(); }, [isOpen]);

  useEffect(() => {
    if (roleToEdit) {
      setRoleName(roleToEdit.role_name || '');
      setDescription(roleToEdit.description || '');
      setHierarchyLevel(roleToEdit.hierarchyLevel || 50);
      const permIds = roleToEdit.permissions?.map(p => typeof p === 'object' ? p._id : p) || [];
      setSelectedPermissions(permIds);
      const appt = roleToEdit.pipelinePermissions?.appointment || {};
      setApptViewStages(appt.viewStages || []);
      setApptUpdateStages(appt.updateStages || []);
      const surg = roleToEdit.pipelinePermissions?.surgical || {};
      setSurgViewStages(surg.viewStages || []);
      setSurgUpdateStages(surg.updateStages || []);
    } else {
      resetForm();
    }
  }, [roleToEdit, isOpen]);

  const resetForm = () => {
    setRoleName(''); setDescription(''); setHierarchyLevel(50);
    setSelectedPermissions([]);
    setApptViewStages([]); setApptUpdateStages([]);
    setSurgViewStages([]); setSurgUpdateStages([]);
  };

  const fetchPermissionsData = async () => {
    try {
      setLoading(true);
      const res = await getPermissionsApi();
      if (res.success && res.data) {
        setPermissionsGrouped(res.data.permissionsGrouped || {});
        setAllPermissions(res.data.allPermissions || []);
        setAppointmentStages(res.data.pipelineStages?.appointment || []);
        setSurgicalStages(res.data.pipelineStages?.surgical || []);
        const initialExpand = {};
        MODULE_ORDER.forEach(m => initialExpand[m] = true);
        setExpandedModules(initialExpand);
      }
    } catch (err) {
      console.error('Failed to load permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Build a map of permName → permission object for fast lookup
  const permByName = {};
  allPermissions.forEach(p => { permByName[p.name] = p; });

  const isPermChecked = (permName) => {
    const p = permByName[permName];
    return p ? selectedPermissions.includes(p._id) : false;
  };

  const togglePermByName = (permName) => {
    const p = permByName[permName];
    if (!p) {
      console.warn(`[RoleModal] Permission "${permName}" not found in system permissions.`);
      return;
    }
    setSelectedPermissions(prev =>
      prev.includes(p._id) ? prev.filter(id => id !== p._id) : [...prev, p._id]
    );
  };

  const toggleModuleByKey = (modKey) => {
    const modPerms = (permissionsGrouped[modKey] || []).filter(p => p.module === modKey);
    const modIds = modPerms.map(p => p._id);
    const allSelected = modIds.every(id => selectedPermissions.includes(id));
    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !modIds.includes(id)));
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...modIds])]);
    }
  };

  const toggleStage = (stageId, setFn) => {
    setFn(prev => prev.includes(stageId) ? prev.filter(s => s !== stageId) : [...prev, stageId]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) return;
    const payload = {
      role_name: roleName.trim(), description,
      hierarchyLevel: Number(hierarchyLevel),
      permissions: selectedPermissions,
      pipelinePermissions: {
        appointment: { viewStages: apptViewStages, updateStages: apptUpdateStages },
        surgical: { viewStages: surgViewStages, updateStages: surgUpdateStages }
      }
    };
    try {
      setLoading(true);
      if (roleToEdit?._id) await updateRoleApi(roleToEdit._id, payload);
      else await createRoleApi(payload);
      onSuccess?.(); onClose(); resetForm();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save role');
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  // Count selected sidebar perms
  const sidebarChecked = SIDEBAR_PERMISSIONS.filter(s => isPermChecked(s.key)).length;
  // Count selected module action perms (non-sidebar)
  const moduleActionChecked = selectedPermissions.filter(id => {
    const p = allPermissions.find(x => x._id === id);
    return p && p.module !== 'sidebar';
  }).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden border border-slate-200">

        {/* ── Header ── */}
        <div className="px-6 py-4 bg-gradient-to-r from-[#0f172a] to-[#1e3a5f] text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-300 flex items-center justify-center border border-blue-400/30">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold">{roleToEdit ? 'Edit Custom Role' : 'Create Custom Role'}</h2>
              <p className="text-xs text-slate-400 mt-0.5">Configure sidebar access, module permissions & pipeline stages</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">

          {/* ── Basic Info ── */}
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex-shrink-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Role Name *</label>
                <input
                  type="text" required placeholder="e.g. Senior Telecaller"
                  value={roleName} onChange={e => setRoleName(e.target.value)}
                  disabled={roleToEdit?.isSystemRole}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold bg-white"
                />
              </div>
              <div>
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Hierarchy Level (1–100)</label>
                <input
                  type="number" min="1" max="100"
                  value={hierarchyLevel} onChange={e => setHierarchyLevel(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold bg-white"
                />
                <p className="text-[10px] text-slate-400 mt-1">100 = Admin, 80 = Head, 50 = Executive</p>
              </div>
              <div>
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Description</label>
                <input
                  type="text" placeholder="Brief role responsibilities"
                  value={description} onChange={e => setDescription(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="flex border-b border-slate-200 bg-white px-6 flex-shrink-0">
            {[
              { key: 'sidebar',     icon: Eye,      label: 'Sidebar Visibility',   count: sidebarChecked,      color: 'text-blue-600 border-blue-600' },
              { key: 'modules',     icon: Lock,      label: 'Module Permissions',   count: moduleActionChecked, color: 'text-indigo-600 border-indigo-600' },
              { key: 'appointment_pipeline', icon: Calendar, label: 'Appointment Stages', count: apptViewStages.length + apptUpdateStages.length, color: 'text-sky-600 border-sky-600' },
              { key: 'surgical_pipeline',    icon: Activity, label: 'Surgical Stages',    count: surgViewStages.length + surgUpdateStages.length, color: 'text-rose-600 border-rose-600' },
            ].map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key} type="button" onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 py-3 px-4 text-[11px] font-black uppercase tracking-widest border-b-2 transition-all ${active ? tab.color : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black ${active ? 'bg-current/10' : 'bg-slate-100 text-slate-500'}`}
                    style={active ? { backgroundColor: 'currentColor', color: 'white', opacity: 1 } : {}}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ── Tab Content ── */}
          <div className="flex-1 overflow-y-auto p-6">

            {/* TAB 1: Sidebar Visibility */}
            {activeTab === 'sidebar' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex gap-3">
                  <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-blue-900">Sidebar Visibility Control</p>
                    <p className="text-xs text-blue-700 mt-0.5">Control which sidebar sections this role can <strong>see</strong>. Uncheck any item to completely hide it from this role's navigation.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {SIDEBAR_PERMISSIONS.map(sp => {
                    const checked = isPermChecked(sp.key);
                    const Icon = sp.icon;
                    return (
                      <button
                        key={sp.key} type="button" onClick={() => togglePermByName(sp.key)}
                        className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-all text-left ${
                          checked
                            ? 'border-blue-400 bg-blue-50 shadow-sm shadow-blue-100'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${checked ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-bold truncate ${checked ? 'text-blue-900' : 'text-slate-700'}`}>{sp.label}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{sp.desc}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}`}>
                          {checked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TAB 2: Module Permissions */}
            {activeTab === 'modules' && (
              <div className="space-y-4">
                <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 flex gap-3">
                  <Lock className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-indigo-900">Module Action Permissions</p>
                    <p className="text-xs text-indigo-700 mt-0.5">Control what actions this role can <strong>perform</strong> within each module. These apply beyond sidebar visibility.</p>
                  </div>
                </div>

                {MODULE_ORDER.map(modKey => {
                  const meta = MODULE_META[modKey] || { label: modKey, icon: Zap, bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-600' };
                  const Icon = meta.icon;
                  // Get permissions for this module from the grouped data
                  const modPerms = (permissionsGrouped[modKey] || []).filter(p =>
                    p.module === modKey && PERM_LABELS[p.name]
                  );
                  if (!modPerms.length) return null;

                  const modIds = modPerms.map(p => p._id);
                  const allSelected = modIds.every(id => selectedPermissions.includes(id));
                  const someSelected = modIds.some(id => selectedPermissions.includes(id));
                  const isExpanded = expandedModules[modKey] !== false;

                  return (
                    <div key={modKey} className={`border-2 rounded-2xl overflow-hidden ${isExpanded ? meta.border : 'border-slate-200'}`}>
                      {/* Module Header */}
                      <div className={`px-4 py-3 flex items-center justify-between ${isExpanded ? meta.bg : 'bg-white'}`}>
                        <button
                          type="button"
                          onClick={() => setExpandedModules(prev => ({ ...prev, [modKey]: !isExpanded }))}
                          className="flex items-center gap-3 flex-1"
                        >
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${meta.bg} ${meta.text} border ${meta.border}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="text-left">
                            <p className={`text-sm font-bold ${meta.text}`}>{meta.label}</p>
                            <p className="text-[10px] text-slate-400">{modPerms.filter(p => selectedPermissions.includes(p._id)).length}/{modPerms.length} permissions selected</p>
                          </div>
                          {someSelected && !allSelected && (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.badge}`}>Partial</span>
                          )}
                          {allSelected && (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.badge}`}>All Selected</span>
                          )}
                          <div className="ml-auto mr-3">
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleModuleByKey(modKey)}
                          className={`text-[11px] font-bold px-3 py-1.5 rounded-lg transition ${allSelected ? `${meta.bg} ${meta.text} border ${meta.border}` : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'}`}
                        >
                          {allSelected ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>

                      {/* Permissions Grid */}
                      {isExpanded && (
                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2.5 bg-white border-t border-slate-100">
                          {modPerms.map(perm => {
                            const isChecked = selectedPermissions.includes(perm._id);
                            const info = PERM_LABELS[perm.name] || { label: perm.name, desc: perm.description };
                            return (
                              <label
                                key={perm._id}
                                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                  isChecked ? `${meta.border} ${meta.bg}` : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                                }`}
                              >
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${isChecked ? `${meta.text} border-current bg-current` : 'border-slate-300 bg-white'}`}
                                  style={isChecked ? { backgroundColor: 'currentColor' } : {}}>
                                  {isChecked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                  <input type="checkbox" checked={isChecked} onChange={() => setSelectedPermissions(prev => prev.includes(perm._id) ? prev.filter(id => id !== perm._id) : [...prev, perm._id])} className="sr-only" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-xs font-bold ${isChecked ? meta.text : 'text-slate-800'}`}>{info.label}</p>
                                  <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{info.desc}</p>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* TAB 3: Appointment Pipeline Stages */}
            {activeTab === 'appointment_pipeline' && (
              <div className="space-y-4">
                <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 flex gap-3">
                  <Calendar className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-sky-900">Appointment Pipeline Stage Access</p>
                    <p className="text-xs text-sky-700 mt-0.5">Control which appointment stages this role can <strong>view</strong> and which they can <strong>update/advance</strong>.</p>
                  </div>
                </div>
                <div className="border-2 border-sky-200 rounded-2xl overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-sky-50 border-b border-sky-200">
                      <tr>
                        <th className="px-4 py-3 text-[11px] font-black uppercase text-sky-800">Appointment Stage</th>
                        <th className="px-4 py-3 text-center text-[11px] font-black uppercase text-sky-800">
                          <span className="flex items-center justify-center gap-1.5"><Eye className="w-3.5 h-3.5" /> View</span>
                        </th>
                        <th className="px-4 py-3 text-center text-[11px] font-black uppercase text-sky-800">
                          <span className="flex items-center justify-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Update</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {appointmentStages.map(stage => (
                        <tr key={stage.id} className="hover:bg-sky-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-slate-800">{stage.label}</span>
                            <code className="ml-2 text-[10px] text-slate-400 font-mono bg-slate-100 px-1.5 py-0.5 rounded">{stage.id}</code>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button type="button" onClick={() => toggleStage(stage.id, setApptViewStages)}
                              className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center mx-auto transition-all ${apptViewStages.includes(stage.id) ? 'border-sky-500 bg-sky-500' : 'border-slate-300 hover:border-sky-400'}`}>
                              {apptViewStages.includes(stage.id) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button type="button" onClick={() => toggleStage(stage.id, setApptUpdateStages)}
                              className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center mx-auto transition-all ${apptUpdateStages.includes(stage.id) ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300 hover:border-indigo-400'}`}>
                              {apptUpdateStages.includes(stage.id) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 4: Surgical Pipeline Stages */}
            {activeTab === 'surgical_pipeline' && (
              <div className="space-y-4">
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex gap-3">
                  <Activity className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-rose-900">Surgical Pipeline Stage Access</p>
                    <p className="text-xs text-rose-700 mt-0.5">Restrict non-surgical staff from viewing or advancing critical OT and surgery stages.</p>
                  </div>
                </div>
                <div className="border-2 border-rose-200 rounded-2xl overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-rose-50 border-b border-rose-200">
                      <tr>
                        <th className="px-4 py-3 text-[11px] font-black uppercase text-rose-800">Surgical Stage</th>
                        <th className="px-4 py-3 text-center text-[11px] font-black uppercase text-rose-800">
                          <span className="flex items-center justify-center gap-1.5"><Eye className="w-3.5 h-3.5" /> View</span>
                        </th>
                        <th className="px-4 py-3 text-center text-[11px] font-black uppercase text-rose-800">
                          <span className="flex items-center justify-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Update</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {surgicalStages.map(stage => (
                        <tr key={stage.id} className="hover:bg-rose-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-slate-800">{stage.label}</span>
                            <code className="ml-2 text-[10px] text-slate-400 font-mono bg-slate-100 px-1.5 py-0.5 rounded">{stage.id}</code>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button type="button" onClick={() => toggleStage(stage.id, setSurgViewStages)}
                              className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center mx-auto transition-all ${surgViewStages.includes(stage.id) ? 'border-rose-500 bg-rose-500' : 'border-slate-300 hover:border-rose-400'}`}>
                              {surgViewStages.includes(stage.id) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button type="button" onClick={() => toggleStage(stage.id, setSurgUpdateStages)}
                              className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center mx-auto transition-all ${surgUpdateStages.includes(stage.id) ? 'border-red-600 bg-red-600' : 'border-slate-300 hover:border-red-400'}`}>
                              {surgUpdateStages.includes(stage.id) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                            </button>
                          </td>
                        </tr>
                      ))}     
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5 text-blue-500" />
                <span><strong className="text-blue-700">{sidebarChecked}</strong> sidebar items visible</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-indigo-500" />
                <span><strong className="text-indigo-700">{moduleActionChecked}</strong> module actions granted</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={onClose}
                className="px-5 py-2.5 text-sm font-bold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-100 transition">
                Cancel
              </button>
              <button type="submit" disabled={loading}
                className="px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-[#143E78] to-[#1a5099] rounded-xl hover:shadow-lg hover:shadow-blue-900/20 transition disabled:opacity-50 flex items-center gap-2">
                {loading ? (
                  <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving…</>
                ) : (
                  <><Shield className="w-4 h-4" />{roleToEdit ? 'Update Role' : 'Create Role'}</>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

